import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaCodeBranch, FaEllipsisH, FaPlus, FaTrash, FaUnlink } from "react-icons/fa";
import { createPortal } from "react-dom";
import Modal from "../../components/Modal";
import Spinner from "../../components/Spinner";
import http from "../../api/apiClient";
import { formatDutchDate } from "../../utils/dutchDate";
import SplitEditor from "./SplitEditor";
import TermPartsEditor from "./TermPartsEditor";
import type { FamilyOption } from "./FamilyPicker";
import { compactIban } from "./financeStudentsColumns";
import { euro, monthLabel, openMonths, type ContributionCell, type ContributionRow, type FamilySummary, type Placement, type Receipt } from "./contributionsModel";

// Shared pieces of the contributions page (per family and per child): progress bar, month strip, open months,
// the payments of a family and the cash payment dialog.

type TFn = ReturnType<typeof useTranslation>["t"];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Soft colours with readable text, for the blocks with amounts in the opened rows. */
const softClass: Record<ContributionCell["status"], string> = {
  PAID: "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200",
  PARTIAL: "bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200",
  OPEN: "bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-200",
  UPCOMING: "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
  NOT_ENROLLED: "border border-dashed border-slate-200 text-slate-300 dark:border-white/10 dark:text-slate-600",
  EXEMPT: "bg-violet-50 text-violet-500 dark:bg-violet-400/10 dark:text-violet-300",
};

const dotClass: Record<ContributionCell["status"], string> = {
  PAID: "bg-emerald-500",
  PARTIAL: "bg-amber-400",
  OPEN: "bg-rose-500",
  UPCOMING: "bg-slate-200 dark:bg-white/15",
  NOT_ENROLLED: "border border-dashed border-slate-300 dark:border-white/15",
  EXEMPT: "bg-violet-200 dark:bg-violet-400/40",
};

export function statusLabel(t: TFn, status: ContributionCell["status"]): string {
  switch (status) {
    case "PAID": return t("finance.contributions.status.paid", { defaultValue: "Paid" });
    case "PARTIAL": return t("finance.contributions.status.partial", { defaultValue: "Partly paid" });
    case "OPEN": return t("finance.contributions.status.open", { defaultValue: "Due, not paid" });
    case "UPCOMING": return t("finance.contributions.status.upcoming", { defaultValue: "Not due yet" });
    case "EXEMPT": return t("finance.contributions.status.exempt", { defaultValue: "Exempt" });
    default: return t("finance.contributions.status.not_enrolled", { defaultValue: "Not at school" });
  }
}

/** Paid against what was due up to now, as one bar that fills up. */
export function ContributionProgress({ row }: { row: Pick<ContributionRow, "expectedToDate" | "paid" | "balance" | "expectedYear"> }) {
  const { t } = useTranslation();
  const expected = Number(row.expectedToDate);
  const paid = Number(row.paid);
  const ratio = expected > 0 ? Math.min(1, paid / expected) : paid > 0 ? 1 : 0;
  const behind = expected > 0 && paid < expected;
  const tone = expected <= 0 ? "bg-slate-300 dark:bg-white/20" : ratio >= 1 ? "bg-emerald-500" : "bg-amber-400";
  const label = t("finance.contributions.progress_label", {
    paid: euro(paid),
    expected: euro(expected),
    year: euro(row.expectedYear),
    defaultValue: "{{paid}} of {{expected}} paid to date (whole school year: {{year}})",
  });
  return (
    <div
      role="progressbar"
      aria-label={label}
      title={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(ratio * 100)}
      data-behind={behind}
      className={`h-1.5 w-full max-w-[12rem] overflow-hidden rounded-full ${behind ? "bg-rose-200 dark:bg-rose-400/25" : "bg-slate-200 dark:bg-white/10"}`}
    >
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

/** "Open: sep okt" for one child (closed rows). */
export function OpenMonths({ cells }: { cells: ContributionCell[] }) {
  const { t } = useTranslation();
  const open = openMonths(cells);
  if (open.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-[11px]" data-open-months={open.map((o) => o.month).join(",")}>
      <span className="font-semibold text-rose-700 dark:text-rose-300">{t("finance.contributions.open_label", { defaultValue: "Open:" })}</span>
      {open.map((item) => (
        <span
          key={item.month}
          title={`${monthLabel(item.month, "long")}: ${euro(item.missing)} ${t("finance.contributions.still_open", { defaultValue: "still open" })}`}
          className={`rounded px-1 py-px font-semibold ${item.partial
            ? "bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200"
            : "bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-200"}`}
        >
          {monthLabel(item.month)}{item.partial ? ` ${euro(item.missing)}` : ""}
        </span>
      ))}
    </span>
  );
}

/**
 * One line for a whole family: "Open: aug (4 children) · sep (5) · okt (5)". With one child only the months.
 */
export function OpenMonthsSummary({ children }: { children: ContributionRow[] }) {
  const { t } = useTranslation();
  const counts = new Map<string, number>();
  children.forEach((child) => openMonths(child.cells).forEach((open) => counts.set(open.month, (counts.get(open.month) ?? 0) + 1)));
  if (counts.size === 0) return null;
  const months = Array.from(counts.keys()).sort();
  const several = children.length > 1;
  return (
    <span className="text-[11px] text-rose-700 dark:text-rose-300" data-open-summary={months.join(",")}>
      <span className="font-semibold">{t("finance.contributions.open_label", { defaultValue: "Open:" })}</span>{" "}
      {months.map((month, index) => {
        const count = counts.get(month) ?? 0;
        const suffix = !several ? ""
          : index === 0
            ? ` (${t("finance.contributions.children_count", { count, defaultValue: "{{count}} children" })})`
            : ` (${count})`;
        return `${index > 0 ? " · " : ""}${monthLabel(month)}${suffix}`;
      }).join("")}
    </span>
  );
}

/**
 * The months of the school year on one line. Opened rows (showAmounts): month above a soft-coloured block with
 * paid/due in it. Closed rows: only small blocks; the amounts appear when you point at one.
 */
/** onCellClick (with showAmounts): the boxes of months the child attends become buttons, for switching a term off or on. */
const wholeEuros = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 });
const euroCents = new Intl.NumberFormat("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The exact amount of a month box, shown enlarged on hover: whole euros stay short (438), others show their cents (437,50). */
export const cellAmount = (value: number | string | null | undefined): string => {
  const cents = Math.round(Number(value ?? 0) * 100);
  return cents % 100 === 0 ? wholeEuros.format(cents / 100) : euroCents.format(cents / 100);
};

export function MonthStrip({ cells, currentMonth, showAmounts = false, onCellClick, onCellHover, highlightMonth = null, focusMonths = null }: {
  cells: ContributionCell[]; currentMonth?: string; showAmounts?: boolean; onCellClick?: (cell: ContributionCell) => void;
  /** Called with the box under the mouse (or with focus), and with null when it leaves. */
  onCellHover?: (cell: ContributionCell | null) => void;
  /** The month hovered, shown with a yellow ring. */
  highlightMonth?: string | null;
  /** While a payment is pointed at: the months it fills stay lit with a green ring, the others are dimmed. */
  focusMonths?: Set<string> | null;
}) {
  const { t } = useTranslation();
  return (
    // With amounts the strip wraps onto a second row when its column is narrow, instead of running over its neighbour.
    <span className={showAmounts ? "flex min-w-0 max-w-full flex-wrap items-end gap-1" : "inline-flex items-center gap-0.5"}>
      {cells.map((cell) => {
        const label = cell.status === "NOT_ENROLLED"
          ? `${monthLabel(cell.month, "long")}: ${statusLabel(t, cell.status)}`
          : `${monthLabel(cell.month, "long")}: ${euro(cell.paid)} / ${euro(cell.expected)} (${statusLabel(t, cell.status)})${cell.adjusted
            ? ` · ${t("finance.adjustments.marker", { defaultValue: "different contribution" })}` : ""}${cell.skipped
            ? ` · ${t("finance.skip.marker", { defaultValue: "term switched off" })}${cell.skipNote ? `: ${cell.skipNote}` : ""}` : ""}${(cell.sources ?? []).length > 0
            ? ` · ${t("finance.contributions.cell_sources", {
              list: (cell.sources ?? []).map((source) => `${formatDutchDate(source.date)} ${euro(source.amount)}`).join(", "),
              defaultValue: "from the payment(s) of {{list}}",
            })}` : ""}`;
        const focused = focusMonths?.has(cell.month) ?? false;
        const current = focused
          ? "ring-2 ring-emerald-500 ring-offset-1 dark:ring-emerald-400 dark:ring-offset-slate-900"
          : cell.month === highlightMonth
            ? "ring-2 ring-amber-400 ring-offset-1 dark:ring-offset-slate-900"
            : cell.month === currentMonth ? "ring-1 ring-indigo-400 ring-offset-1 dark:ring-offset-slate-900" : "";
        const dimmed = focusMonths !== null && !focused;
        if (!showAmounts) {
          return (
            <span key={cell.month} role="img" aria-label={label} title={label} data-status={cell.status}
              className={`h-3.5 w-3.5 rounded-[3px] ${dotClass[cell.status]} ${current}`} />
          );
        }
        const boxClass = `flex h-6 w-full items-center justify-center whitespace-nowrap px-1 rounded-md text-[10px] font-semibold tabular-nums transition-shadow duration-200 ${softClass[cell.status]} ${current} ${cell.adjusted ? "border-b-2 border-violet-300 dark:border-violet-400/60" : ""}`;
        const content = cell.status === "NOT_ENROLLED" ? "–"
          : cell.skipped ? t("finance.skip.short", { defaultValue: "off" })
            : cell.status === "EXEMPT" ? t("finance.adjustments.exempt_short", { defaultValue: "free" })
              : `${cellAmount(cell.paid)}/${cellAmount(cell.expected)}`;
        // Keep cents visible so the month amounts agree with the totals; hover enlarges the same values.
        const exact = cell.status === "NOT_ENROLLED" || cell.skipped || cell.status === "EXEMPT" ? null
          : `${cellAmount(cell.paid)}/${cellAmount(cell.expected)}`;
        const enlarged = exact && (
          // An opaque layer under the soft colour, so the box behind does not show through.
          <span aria-hidden="true" data-cell-exact=""
            className="pointer-events-none absolute bottom-0 left-1/2 z-20 hidden -translate-x-1/2 rounded-md bg-white shadow-lg ring-1 ring-black/10 group-hover:flex group-focus-within:flex dark:bg-slate-900 dark:ring-white/10">
            <span className={`whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold tabular-nums ${softClass[cell.status]}`}>{exact}</span>
          </span>
        );
        const clickable = onCellClick !== undefined && cell.status !== "NOT_ENROLLED";
        return (
          <span key={cell.month} data-cell-month={cell.month} data-dimmed={dimmed ? "true" : undefined} data-focused={focused ? "true" : undefined}
            className={`group relative flex min-w-12 w-auto flex-col items-center gap-0.5 transition-[opacity,filter] duration-150 ${dimmed ? "opacity-30 grayscale" : ""}`}
            onMouseEnter={onCellHover ? () => onCellHover(cell) : undefined}
            onMouseLeave={onCellHover ? () => onCellHover(null) : undefined}>
            <span className="text-[9px] font-bold uppercase leading-none text-slate-500">{monthLabel(cell.month)}</span>
            {clickable ? (
              <button
                type="button"
                aria-label={label}
                title={onCellHover ? undefined : label}
                data-status={cell.status}
                data-adjusted={cell.adjusted ? "true" : undefined}
                data-skipped={cell.skipped ? "true" : undefined}
                data-cell-box=""
                onFocus={onCellHover ? () => onCellHover(cell) : undefined}
                onBlur={onCellHover ? () => onCellHover(null) : undefined}
                onClick={() => onCellClick?.(cell)}
                className={`${boxClass} cursor-pointer transition hover:ring-2 hover:ring-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:hover:ring-indigo-400/60`}
              >
                {content}
              </button>
            ) : (
              <span
                role="img"
                aria-label={label}
                title={onCellHover ? undefined : label}
                data-status={cell.status}
                data-adjusted={cell.adjusted ? "true" : undefined}
                data-skipped={cell.skipped ? "true" : undefined}
                data-cell-box=""
                className={boxClass}
              >
                {content}
              </span>
            )}
            {enlarged}
          </span>
        );
      })}
    </span>
  );
}

export function Legend() {
  const { t } = useTranslation();
  const items: Array<ContributionCell["status"]> = ["PAID", "PARTIAL", "OPEN", "UPCOMING", "EXEMPT", "NOT_ENROLLED"];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 px-5 py-2.5 text-[11px] text-slate-500 dark:border-white/10 dark:text-slate-400">
      {items.map((status) => (
        <span key={status} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-[3px] ${dotClass[status]}`} />
          {statusLabel(t, status)}
        </span>
      ))}
    </div>
  );
}

/**
 * "J. de Vries · •••• 6789": the name the backend chose (payer, parent of the bank account, family or account holder
 * on the bank line), or "Unknown payer" when there is none.
 */
export function payerLabel(t: TFn, receipt: Pick<Receipt, "relationName" | "payerIbanMasked"> & Partial<Pick<Receipt, "payerName" | "payerNameSource">>): string {
  const iban = receipt.payerIbanMasked ? compactIban(receipt.payerIbanMasked) : null;
  const name = receipt.payerName
    ? receipt.payerNameSource === "FAMILY"
      ? t("finance.payments.payer_family", { name: receipt.payerName, defaultValue: "Family {{name}}" })
      : receipt.payerName
    : receipt.relationName || t("finance.payments.unknown_payer", { defaultValue: "Unknown payer" });
  return iban ? `${name} · ${iban}` : name;
}

export function linkMethodLabel(t: TFn, method?: Receipt["linkMethod"]): string {
  switch (method) {
    case "IBAN": return t("finance.payments.linked_by.iban", { defaultValue: "linked by IBAN" });
    case "RELATION": return t("finance.payments.linked_by.relation", { defaultValue: "linked by an earlier link of this payer" });
    case "MANUAL": return t("finance.payments.linked_by.manual", { defaultValue: "linked by hand" });
    case "SPLIT": return t("finance.payments.divided", { defaultValue: "Divided payment" });
    default: return t("finance.payments.linked_by.none", { defaultValue: "not linked" });
  }
}

export type ReceiptChild = { childId: string; name: string };

/** Months (YYYY-MM) grouped per school year (August–July), labelled "2025–2026". */
function groupByYear(months: string[]): Array<[string, string[]]> {
  const groups = new Map<string, string[]>();
  for (const month of months) {
    const [year, m] = month.split("-").map(Number);
    const start = m >= 8 ? year : year - 1;
    const key = `${start}–${start + 1}`;
    groups.set(key, [...(groups.get(key) ?? []), month]);
  }
  return Array.from(groups.entries());
}

/**
 * Which term (month) a payment is for. "Automatic" means: a month named in the description, otherwise the month
 * of payment; its current outcome is shown in the option. Months of other school years may be offered too: a payment
 * made in August for July then counts in the school year before.
 */
export function TermSelect({ months, value, automaticMonth, onChange, label, className = "" }: {
  months: string[];
  value: string;
  automaticMonth?: string | null;
  onChange: (month: string) => void;
  label: string;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}
      className={`h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm dark:border-white/10 dark:bg-slate-900 ${className}`}>
      <option value="">
        {automaticMonth
          ? t("finance.payments.term_automatic_month", { month: monthLabel(automaticMonth, "long"), defaultValue: "Automatic ({{month}})" })
          : t("finance.payments.term_automatic", { defaultValue: "Automatic" })}
      </option>
      {groupByYear(months).map(([yearLabel, yearMonths]) => (
        <optgroup key={yearLabel} label={yearLabel}>
          {yearMonths.map((month) => <option key={month} value={month}>{monthLabel(month, "long")}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

/** "term okt" with a hint where it comes from. */
function termText(t: TFn, receipt: Receipt): string | null {
  if (!receipt.termMonth) return null;
  return t("finance.payments.term_short", { month: monthLabel(receipt.termMonth), defaultValue: "term {{month}}" });
}

function termTitle(t: TFn, receipt: Receipt): string {
  switch (receipt.termSource) {
    case "CHOSEN": return t("finance.payments.term_source.chosen", { defaultValue: "Term chosen by hand" });
    case "DESCRIPTION": return t("finance.payments.term_source.description", { defaultValue: "Term taken from the description" });
    default: return t("finance.payments.term_source.date", { defaultValue: "Term = month of payment" });
  }
}

/**
 * Small "⋯" menu for the actions on one payment. Rendered on the page itself (not inside the card), so a card that
 * hides its overflow does not cut it off; it opens upwards when there is no room below.
 */
function RowMenu({ label, items }: { label: string; items: Array<{ key: string; label: string; icon: ReactElement; onSelect: () => void; danger?: boolean }> }) {
  const [position, setPosition] = useState<{ top: number; right: number; up: boolean } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLSpanElement>(null);
  const open = position !== null;

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const height = menuRef.current?.offsetHeight ?? items.length * 32 + 8;
    const up = rect.bottom + 4 + height > window.innerHeight && rect.top - 4 - height > 0;
    setPosition({ top: up ? rect.top - 4 : rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right), up });
  }, [items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: Event) => {
      const target = event.target as Node | null;
      if (target && (menuRef.current?.contains(target) || buttonRef.current?.contains(target))) return;
      setPosition(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPosition(null);
    };
    const closeNow = () => setPosition(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", closeNow, true);
    window.addEventListener("resize", closeNow);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", closeNow, true);
      window.removeEventListener("resize", closeNow);
    };
  }, [open]);

  // Once the menu is there its real height is known: place it again (it may have to open upwards).
  useEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (items.length === 0) return null;
  return (
    <span className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => (open ? setPosition(null) : place())}
        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
      >
        <FaEllipsisH aria-hidden="true" />
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <span
          ref={menuRef}
          role="menu"
          style={{ position: "fixed", top: position.top, right: position.right, transform: position.up ? "translateY(-100%)" : undefined }}
          className="z-[9999] flex min-w-[12rem] flex-col rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-white/10 dark:bg-slate-900"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => {
                setPosition(null);
                item.onSelect();
              }}
              className={`flex items-center gap-2 whitespace-nowrap px-3 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-white/5 ${item.danger ? "text-rose-600" : "text-slate-700 dark:text-slate-200"}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </span>,
        document.body,
      )}
    </span>
  );
}

/**
 * The payments of a family in a school year: a compact list (date, amount, from whom, for whom) with the actions
 * behind "⋯", and a button for the rare cash payment, which opens a small dialog.
 */
export function FamilyPayments({ family, schoolYearId, canManage, onChanged, childOptions = [], defaultChildId = "", familyOptions, months = [], windowMonths = [], placements, highlighted, onReceiptHover, selectedReceiptId, onReceiptSelect }: {
  family: Pick<FamilySummary, "familyId" | "familyName">;
  schoolYearId: string;
  canManage: boolean;
  onChanged: () => void;
  /** Children of the family a cash payment can be meant for. */
  childOptions?: ReceiptChild[];
  /** Child chosen by default in the cash dialog ("" = the whole family). */
  defaultChildId?: string;
  /** All families with their children; when given, imported payments can be divided here. */
  familyOptions?: FamilyOption[];
  /** Months of the school year, to choose the term of a payment. */
  months?: string[];
  /** Months that may be used for a division over several school years (current, previous and next). */
  windowMonths?: string[];
  /** Per payment: the months of the children it fills, to show where the money went. */
  placements?: Map<string, Placement[]>;
  /** Payments to mark, for example those that fill the month clicked on. */
  highlighted?: Set<string>;
  /** Called with the payment under the mouse, and with null when it leaves; its months light up. */
  onReceiptHover?: (receiptId: string | null) => void;
  /** The payment clicked open (it shows everything); without these props the list keeps it itself. */
  selectedReceiptId?: string | null;
  onReceiptSelect?: (receiptId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [receipts, setReceipts] = useState<Receipt[] | null>(null);
  const [choosingTerm, setChoosingTerm] = useState<string | null>(null);
  // A term may also lie in the previous or next school year (paid in August for July).
  const termMonths = windowMonths.length > 0 ? windowMonths : months;
  const [splitting, setSplitting] = useState<string | null>(null);
  const [dividing, setDividing] = useState<string | null>(null);
  const [cashOpen, setCashOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);
  const childNames = new Map(childOptions.map((child) => [child.childId, child.name]));
  const listRef = useRef<HTMLUListElement>(null);
  const [ownSelected, setOwnSelected] = useState<string | null>(null);
  const selected = selectedReceiptId !== undefined ? selectedReceiptId : ownSelected;
  const select = onReceiptSelect ?? setOwnSelected;
  const highlightKey = highlighted ? Array.from(highlighted).sort().join(",") : "";

  // Bring the first marked payment into view.
  useEffect(() => {
    if (!highlightKey) return;
    const first = listRef.current?.querySelector<HTMLElement>('[data-highlighted="true"]');
    first?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [highlightKey, receipts]);

  const load = useCallback(() => {
    http.get(`/finance/contributions/families/${family.familyId}/receipts`, { params: { schoolYearId } })
      .then(({ data }) => setReceipts(Array.isArray(data) ? data : []))
      .catch(() => setReceipts([]));
  }, [family.familyId, schoolYearId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    if (busy.current) return;
    busy.current = true;
    setMessage(null);
    try {
      await action();
      load();
      onChanged();
    } catch {
      setMessage(t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." }));
    } finally {
      busy.current = false;
    }
  };

  return (
    <div className="text-sm">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
          {t("finance.contributions.receipts", { defaultValue: "Payments" })}
        </h3>
        {canManage && (
          <button
            type="button"
            onClick={() => setCashOpen(true)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-white/5"
          >
            <FaPlus aria-hidden="true" />
            {t("finance.payments.cash_button", { defaultValue: "Cash payment" })}
          </button>
        )}
      </div>
      {receipts === null ? (
        <Spinner />
      ) : receipts.length === 0 ? (
        <p className="text-xs text-slate-500">{t("finance.contributions.no_receipts", { defaultValue: "No payments yet in this school year." })}</p>
      ) : (
        <ul ref={listRef} className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-slate-900">
          {receipts.map((receipt) => {
            const split = receipt.linkMethod === "SPLIT";
            const ownParts = (receipt.allocations ?? []).filter((part) => part.familyId === family.familyId);
            const share = split ? ownParts.reduce((sum, part) => sum + Number(part.amount), 0) : Number(receipt.amount);
            const manual = receipt.source === "MANUAL";
            const parts = receipt.termParts ?? [];
            const notDivided = Number(receipt.amount) - parts.reduce((sum, part) => sum + Number(part.amount), 0);
            const placed = placements?.get(receipt.id) ?? [];
            const expanded = selected === receipt.id;
            // Opened, the row grows and shows its full texts instead of cutting them off.
            const clip = expanded ? "whitespace-normal break-words" : "truncate";
            const placedText = placed.map((place) => `${place.childName} ${monthLabel(place.month)} ${euro(place.amount)}`).join(", ");
            const from = manual ? t("finance.payments.cash", { defaultValue: "Cash" }) : payerLabel(t, receipt);
            const detail = manual ? receipt.note : receipt.description;
            const forWhom = split
              ? ownParts.map((part) => (part.childId ? `${childNames.get(part.childId) || "?"} ${euro(part.amount)}` : null)).filter(Boolean).join(", ")
              : receipt.childId ? childNames.get(receipt.childId) || "" : "";
            const items = !canManage ? [] : [
              ...(termMonths.length > 0 && parts.length === 0 ? [{ key: "term", label: t("finance.payments.term_choose", { defaultValue: "Choose term" }), icon: <FaCalendarAlt aria-hidden="true" />,
                onSelect: () => setChoosingTerm(receipt.id) }] : []),
              ...(!split && windowMonths.length > 0 ? [{ key: "parts", label: t("finance.payments.parts.open", { defaultValue: "Divide over months" }), icon: <FaCalendarAlt aria-hidden="true" />,
                onSelect: () => setDividing(receipt.id) }] : []),
              ...(!manual && familyOptions ? [{ key: "split", label: t("finance.payments.split.open", { defaultValue: "Divide payment" }), icon: <FaCodeBranch aria-hidden="true" />, onSelect: () => setSplitting(receipt.id) }] : []),
              ...(!manual ? [{ key: "unlink", label: t("finance.payments.unlink", { defaultValue: "Unlink payment" }), icon: <FaUnlink aria-hidden="true" />,
                onSelect: () => run(() => http.post(`/finance/receipts/${receipt.id}/unlink`, { forget: true })) }] : []),
              ...(manual ? [{ key: "delete", label: t("finance.contributions.delete_receipt", { defaultValue: "Delete receipt" }), icon: <FaTrash aria-hidden="true" />, danger: true,
                onSelect: () => run(() => http.delete(`/finance/receipts/${receipt.id}`)) }] : []),
            ];
            return (
              <Fragment key={receipt.id}>
                <li data-receipt={receipt.id} title={manual || expanded ? undefined : linkMethodLabel(t, receipt.linkMethod)}
                  data-highlighted={highlighted?.has(receipt.id) ? "true" : undefined}
                  data-expanded={expanded ? "true" : "false"}
                  tabIndex={0}
                  onMouseEnter={onReceiptHover ? () => onReceiptHover(receipt.id) : undefined}
                  onMouseLeave={onReceiptHover ? () => onReceiptHover(null) : undefined}
                  onClick={(event) => {
                    // The ⋯ menu and other controls in the row do their own thing.
                    if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) return;
                    select(expanded ? null : receipt.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    select(expanded ? null : receipt.id);
                  }}
                  className={`grid cursor-pointer grid-cols-[4.5rem_4.25rem_minmax(0,1fr)_auto] gap-2 px-2 py-1.5 text-xs transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400 ${expanded ? "items-start" : "items-center hover:bg-slate-50 dark:hover:bg-white/5"} ${highlighted?.has(receipt.id)
                    ? "bg-amber-50 shadow-[inset_4px_0_0_0_#f59e0b] ring-1 ring-inset ring-amber-300 dark:bg-amber-400/10 dark:shadow-[inset_4px_0_0_0_#fcd34d] dark:ring-amber-400/40"
                    : expanded ? "bg-emerald-50/70 shadow-[inset_4px_0_0_0_#10b981] dark:bg-emerald-400/10 dark:shadow-[inset_4px_0_0_0_#34d399]" : ""}`}>
                  <span className="tabular-nums text-slate-500">
                    {formatDutchDate(receipt.date)}
                    {termText(t, receipt) && (
                      <span className={`block text-[10px] ${receipt.termSource === "CHOSEN" ? "font-semibold text-indigo-600 dark:text-indigo-300" : "text-slate-400"}`}
                        title={termTitle(t, receipt)}>
                        {termText(t, receipt)}
                      </span>
                    )}
                  </span>
                  <span className="text-right font-semibold tabular-nums text-slate-900 dark:text-white">
                    {euro(share)}
                    {split && <span className="block text-[10px] font-normal text-slate-500">{t("finance.payments.part_of", { amount: euro(receipt.amount), defaultValue: "of {{amount}}" })}</span>}
                  </span>
                  <span className="min-w-0">
                    <span className={`block ${clip} text-slate-800 dark:text-slate-100`}>
                      {from}
                      {forWhom && <span className="text-slate-500"> · {t("finance.payments.for_child", { name: forWhom, defaultValue: "For {{name}}" })}</span>}
                      {receipt.ignored && <span className="text-slate-500"> · {t("finance.payments.not_counted", { defaultValue: "not counted" })}</span>}
                    </span>
                    {detail && <span data-receipt-detail="" className={`block ${clip} text-[11px] text-slate-500`} title={expanded ? undefined : detail}>{detail}</span>}
                    {placements && !receipt.ignored && parts.length === 0 && (
                      <span data-placed={receipt.id} className={`block ${clip} text-[11px] ${placed.length > 0 ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}
                        title={placedText || undefined}>
                        {placed.length > 0
                          ? `→ ${placedText}`
                          : t("finance.payments.not_in_month", { defaultValue: "→ not in a month (paid in advance or extra)" })}
                      </span>
                    )}
                    {parts.length > 0 && (
                      <span className="block text-[11px] text-indigo-600 dark:text-indigo-300">
                        {t("finance.payments.parts.divided_line", { months: Array.from(new Set(parts.map((part) => monthLabel(part.termMonth)))).join(", "), defaultValue: "divided: {{months}}" })}
                        {" · "}
                        {t("finance.payments.parts.not_divided_line", { amount: euro(notDivided), defaultValue: "not divided: {{amount}}" })}
                      </span>
                    )}
                    {expanded && (
                      <span className="mt-1 block text-[11px] text-slate-500">
                        {[manual ? null : linkMethodLabel(t, receipt.linkMethod), receipt.termMonth ? termTitle(t, receipt) : null].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                  <RowMenu label={t("finance.payments.actions", { defaultValue: "Actions" })} items={items} />
                </li>
                {choosingTerm === receipt.id && (
                  <li className="flex flex-wrap items-center gap-2 px-2.5 py-2 text-xs">
                    <span className="font-semibold text-slate-600 dark:text-slate-300">
                      {t("finance.payments.term_question", { defaultValue: "For which term?" })}
                    </span>
                    <TermSelect
                      months={termMonths}
                      value={receipt.termSource === "CHOSEN" ? receipt.termMonth || "" : ""}
                      automaticMonth={receipt.termSource === "CHOSEN" ? null : receipt.termMonth}
                      label={t("finance.payments.term_for", { amount: euro(receipt.amount), defaultValue: "Term for the payment of {{amount}}" })}
                      onChange={(month) => {
                        setChoosingTerm(null);
                        run(() => http.put(`/finance/receipts/${receipt.id}/term`, { termMonth: month || null }));
                      }}
                    />
                    <button type="button" onClick={() => setChoosingTerm(null)} className="text-slate-500 hover:underline">
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </button>
                  </li>
                )}
                {dividing === receipt.id && (
                  <li className="px-2.5 py-2">
                    <TermPartsEditor
                      receipt={receipt}
                      childOptions={childOptions}
                      months={windowMonths}
                      onCancel={() => setDividing(null)}
                      onDone={() => {
                        setDividing(null);
                        load();
                        onChanged();
                      }}
                    />
                  </li>
                )}
                {splitting === receipt.id && familyOptions && (
                  <li className="px-2.5 py-2">
                    <SplitEditor
                      receipt={receipt}
                      families={familyOptions}
                      onCancel={() => setSplitting(null)}
                      onDone={() => {
                        setSplitting(null);
                        load();
                        onChanged();
                      }}
                    />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
      )}
      {message && <p role="alert" className="mt-1 text-xs font-semibold text-rose-600">{message}</p>}
      {canManage && (
        <CashPaymentDialog
          show={cashOpen}
          familyId={family.familyId}
          familyName={family.familyName || ""}
          schoolYearId={schoolYearId}
          childOptions={childOptions}
          defaultChildId={defaultChildId}
          months={months}
          onClose={() => setCashOpen(false)}
          onSaved={() => {
            setCashOpen(false);
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** A small dialog for the rare cash payment: date, amount, note and (with several children) for whom. */
function CashPaymentDialog({ show, familyId, familyName, schoolYearId, childOptions, defaultChildId, months, onClose, onSaved }: {
  show: boolean;
  familyId: string;
  familyName: string;
  schoolYearId: string;
  childOptions: ReceiptChild[];
  defaultChildId: string;
  months: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [termMonth, setTermMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [childId, setChildId] = useState(defaultChildId);
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    if (show) {
      setDate(todayIso());
      setAmount("");
      setNote("");
      setChildId(defaultChildId);
      setTermMonth("");
      setMessage(null);
    }
  }, [show, defaultChildId]);

  const errorText = (code?: string) => {
    if (code === "RECEIPT_AMOUNT_INVALID") return t("finance.contributions.errors.amount", { defaultValue: "Enter an amount above zero with at most two decimals." });
    if (code === "RECEIPT_NOTE_REQUIRED") return t("finance.contributions.errors.note", { defaultValue: "A note is required, for example \"cash at school\"." });
    return t("finance.contributions.errors.save", { defaultValue: "Saving failed. Please try again." });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setMessage(null);
    try {
      await http.post("/finance/receipts", {
        familyId,
        schoolYearId,
        date,
        amount: amount.replace(",", "."),
        note,
        childId: childId || null,
        termMonth: termMonth || null,
      });
      onSaved();
    } catch (caught: any) {
      setMessage(errorText(caught?.response?.data?.message));
    } finally {
      busy.current = false;
    }
  };

  const input = "h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm dark:border-white/10 dark:bg-slate-900";
  return (
    <Modal
      show={show}
      onClose={onClose}
      hideDefaultActions
      showIcon={false}
      title={t("finance.payments.cash_title", { name: familyName, defaultValue: "Cash payment · family {{name}}" })}
    >
      <form onSubmit={submit} className="grid gap-3 text-left text-sm">
        {childOptions.length > 1 && (
          <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.payments.for_whom", { defaultValue: "For" })}
            <select value={childId} onChange={(e) => setChildId(e.target.value)} className={input}>
              <option value="">{t("finance.payments.whole_family", { defaultValue: "The whole family (oldest month and oldest child first)" })}</option>
              {childOptions.map((child) => <option key={child.childId} value={child.childId}>{child.name}</option>)}
            </select>
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.contributions.date", { defaultValue: "Date" })}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className={input} />
          </label>
          <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.contributions.amount", { defaultValue: "Amount (€)" })}
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="0,00" className={input} />
          </label>
        </div>
        {months.length > 0 && (
          <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.payments.term_question", { defaultValue: "For which term?" })}
            <TermSelect months={months} value={termMonth} automaticMonth={date ? date.slice(0, 7) : null} onChange={setTermMonth}
              label={t("finance.payments.term_question", { defaultValue: "For which term?" })} className="h-10" />
          </label>
        )}
        <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
          {t("finance.contributions.note", { defaultValue: "Note (required)" })}
          <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} required className={input} />
        </label>
        {message && <p role="alert" className="text-sm font-semibold text-rose-600">{message}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 dark:border-white/10 dark:text-slate-300">
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="submit" className="h-10 rounded-lg bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700">
            {t("finance.contributions.add", { defaultValue: "Add" })}
          </button>
        </div>
      </form>
    </Modal>
  );
}
